document.documentElement.classList.add('js');

const initFactoryPlaylists = (root = document) => {
  root.querySelectorAll('[data-factory-playlist]').forEach((playlist) => {
    if (playlist.dataset.playlistReady === 'true') return;

    const section = playlist.closest('.custom-factory');
    const player = section?.querySelector('[data-factory-video-player]');
    const items = Array.from(playlist.querySelectorAll('[data-factory-playlist-item]'));
    if (!player || !items.length) return;

    playlist.dataset.playlistReady = 'true';
    items.forEach((item) => {
      item.addEventListener('click', () => {
        const template = item.querySelector('[data-factory-video-template]');
        if (!template) return;

        player.replaceChildren(template.content.cloneNode(true));
        items.forEach((candidate) => candidate.classList.toggle('is-active', candidate === item));

        const video = player.querySelector('video');
        video?.play().catch(() => {});
        player.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });
  });
};

const initProductExplorerRows = (root = document) => {
  root.querySelectorAll('[data-explorer-row]').forEach((row) => {
    if (row.dataset.explorerReady === 'true') return;

    const track = row.querySelector('[data-explorer-track]');
    const previous = row.querySelector('[data-explorer-prev]');
    const next = row.querySelector('[data-explorer-next]');
    if (!track || !previous || !next) return;

    row.dataset.explorerReady = 'true';
    const move = (direction) => {
      const card = track.querySelector('.product-explorer-card');
      const distance = card ? card.getBoundingClientRect().width + 15 : track.clientWidth * .75;
      track.scrollBy({ left: direction * distance, behavior: 'smooth' });
    };

    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      let timer;
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        timer = window.setInterval(() => {
          const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 8;
          if (atEnd) track.scrollTo({ left: 0, behavior: 'smooth' });
          else move(1);
        }, 5200);
      };

      row.addEventListener('mouseenter', stop);
      row.addEventListener('mouseleave', start);
      row.addEventListener('focusin', stop);
      row.addEventListener('focusout', start);
      start();
    }
  });
};

const updateHeaderCartCount = (cart) => {
  document.querySelectorAll('a[href$="/cart"], a[href$="/cart/"]').forEach((link) => {
    if (/Cart\s*\(\d+\)/i.test(link.textContent)) {
      link.textContent = link.textContent.replace(/Cart\s*\(\d+\)/i, `Cart (${cart.item_count})`);
    }
  });
  document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
};

const initAjaxProductForms = (root = document) => {
  root.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
    if (form.dataset.ajaxCartReady === 'true') return;
    form.dataset.ajaxCartReady = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.ajaxCartLoading === 'true') return;

      const button = form.querySelector('[type="submit"]');
      const error = form.querySelector('[data-add-cart-error]');
      const originalLabel = button?.textContent;
      form.dataset.ajaxCartLoading = 'true';
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.classList.add('ajax-cart-loading');
        button.textContent = 'ADDING…';
      }

      try {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const addResponse = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        });
        const addResult = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok) throw new Error(addResult.description || addResult.message || "We couldn't add this item to your cart. Please try again.");

        const cartResponse = await fetch(`${rootUrl}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store'
        });
        if (!cartResponse.ok) throw new Error('The item was added, but the cart quantity could not be updated. Please refresh the page.');
        const cart = await cartResponse.json();
        updateHeaderCartCount(cart);

        const modal = document.querySelector('[data-added-cart-modal]');
        if (modal) {
          modal.hidden = false;
          modal.querySelector('[data-added-cart-panel]')?.focus();
        }
      } catch (requestError) {
        if (error) {
          error.textContent = requestError.message || "We couldn't add this item to your cart. Please try again.";
          error.hidden = false;
        }
      } finally {
        form.dataset.ajaxCartLoading = 'false';
        if (button) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.classList.remove('ajax-cart-loading');
          button.textContent = originalLabel;
        }
      }
    });
  });
};

const initAddedCartModal = (root = document) => {
  const modal = root.querySelector('[data-added-cart-modal]');
  if (!modal || modal.dataset.modalReady === 'true') return;
  modal.dataset.modalReady = 'true';
  let returnFocus = null;

  const close = () => {
    if (modal.hidden) return;
    modal.hidden = true;
    returnFocus?.focus();
  };

  modal.querySelectorAll('[data-added-cart-close]').forEach((control) => control.addEventListener('click', close));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !modal.hidden) close();
  });
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[action*="/cart/add"]')) returnFocus = event.submitter || event.target.querySelector('[type="submit"]');
  }, true);
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-custom-slider]').forEach((slider) => {
    const slides = Array.from(slider.querySelectorAll('[data-custom-slide]'));
    const dots = slider.querySelector('[data-slide-dots]');
    if (!slides.length || !dots) return;
    let current = 0;
    let timer;
    const dotItems = slides.map((_, index) => {
      const dot = document.createElement('i');
      dot.setAttribute('aria-hidden', 'true');
      dot.addEventListener('click', () => show(index));
      dots.appendChild(dot);
      return dot;
    });
    const show = (index) => {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === current));
      dotItems.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
      window.clearInterval(timer);
      timer = window.setInterval(() => show(current + 1), 6500);
    };
    slider.querySelector('[data-slide-prev]')?.addEventListener('click', () => show(current - 1));
    slider.querySelector('[data-slide-next]')?.addEventListener('click', () => show(current + 1));
    show(0);
  });

  document.querySelectorAll('[data-client-carousel]').forEach((carousel) => {
    const track = carousel.querySelector('[data-client-track]');
    if (!track) return;
    const move = (direction) => track.scrollBy({ left: direction * track.clientWidth * .78, behavior: 'smooth' });
    carousel.querySelector('[data-client-prev]')?.addEventListener('click', () => move(-1));
    carousel.querySelector('[data-client-next]')?.addEventListener('click', () => move(1));
  });

  initFactoryPlaylists();
  initProductExplorerRows();
  initAjaxProductForms();
  initAddedCartModal();
});

document.addEventListener('shopify:section:load', (event) => {
  initFactoryPlaylists(event.target);
  initProductExplorerRows(event.target);
  initAjaxProductForms(event.target);
  initAddedCartModal(event.target);
});
